import { useSelector } from 'react-redux'
import { profileData, ProfileInfo } from '../store/slices/profileSlice'

export interface ProfileInformation {
  // Display strings
  userInfo: string
  userNuid: string
  primaryApproverInfo: string
  employee: ProfileInfo['employee'] | null
  primaryApprover: ProfileInfo['approvers'][number] | null
  roles: ProfileInfo['roles'] | null
  isProfileLoading: boolean
  profileError: string | null
  isAdmin: boolean
  isApprover: boolean
  isApproverProxy: boolean
  isRequestorProxy: boolean
}

/**
 * Formats primary approver information for display.
 * Handles "NA" sentinel values gracefully.
 *
 * @param primaryApprover - The primary approver record or null/undefined
 * @returns Formatted approver string or "N/A" / "NA"
 */
export const formatPrimaryApproverDisplay = (
  primaryApprover: ProfileInfo['approvers'][number] | null | undefined
): string => {
  if (!primaryApprover) return 'N/A'

  const firstName = primaryApprover.approverFirstName?.trim() || ''
  const lastName = primaryApprover.approverLastName?.trim() || ''
  const nuid = primaryApprover.approverNuid?.trim() || ''

  const isFirstNameNa = !firstName || firstName.toUpperCase() === 'NA'
  const isLastNameNa = !lastName || lastName.toUpperCase() === 'NA'
  const isNuidNa = !nuid || nuid.toUpperCase() === 'NA'

  // If all values are NA (or empty), return 'NA'
  if (isFirstNameNa && isLastNameNa && isNuidNa) {
    return 'NA'
  }

  const validFirstName = firstName && firstName.toUpperCase() !== 'NA' ? firstName : ''
  const validLastName = lastName && lastName.toUpperCase() !== 'NA' ? lastName : ''
  const name = [validFirstName, validLastName].filter(Boolean).join(' ')

  if (name) {
    return `${name} (${nuid || 'NA'})`
  }

  return `(${nuid})`
}

/**
 * Custom hook to extract and format profile information from Redux store.
 * @returns ProfileInformation object with formatted strings and flags
 */
export const useProfileDisplay = (): ProfileInformation => {
  // Only read from Redux - no API dispatch here
  const profile = useSelector(profileData)
  const isProfileLoading = useSelector((state: any) => state.profile.loading)
  const profileError = useSelector((state: any) => state.profile.error)

  // Extract profile components
  const employee = profile?.employee ?? null
  const approvers = profile?.approvers ?? []
  const primaryApprover = approvers.length > 0 ? approvers[0] : null
  const roles = profile?.roles ?? null

  // Format user info with full name (first, middle if exists, last)
  const userInfo = employee
    ? `${employee.empFirstName} ${employee.empMiddleName || ''} ${employee.empLastName}`.trim()
    : ''
  const userNuid = employee?.empNuid ? ` (${employee.empNuid})` : ''

  // Format primary approver info
  const primaryApproverInfo = formatPrimaryApproverDisplay(primaryApprover)

  return {
    userInfo,
    userNuid,
    primaryApproverInfo,
    employee,
    primaryApprover,
    roles,
    isProfileLoading,
    profileError,
    isAdmin: roles?.isAdmin ?? false,
    isApprover: roles?.isApprover ?? false,
    isApproverProxy: roles?.isApproverProxy ?? false,
    isRequestorProxy: roles?.isRequestorProxy ?? false,
  }
}
